import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { Editor } from 'primereact/editor';
import { Skeleton } from "primereact/skeleton";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { useDispatch } from "react-redux";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const PrivacyPolicyPage: React.FC = () => {
    const navigate = useNavigate();
    const initialized = useRef(false);
    const { showToast }: any = useToast();
    const dispatch = useDispatch();

    const [englishText, setEnglishText] = useState('');
    const [gujaratiText, setGujaratiText] = useState('');
    const [loading, setLoading] = useState(true);

    async function getCountryData() {
        try {
            let url;
            url = `/cmspages/webapp/PrivacyPolicy`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        setEnglishText(response.data.data.content);
                        setGujaratiText(response.data.data.gujaraticontent);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getCountryData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/settings');
    }

    const handleAdd = async () => {
        let payload = {
            content: englishText,
            gujaraticontent: gujaratiText
        }
        await apiFunctions.patch(`/cmspages/PrivacyPolicy`, payload)
            .then((response) => {
                if (response && response.data && response.data.success) {
                    showToast({
                        severity: "success",
                        summary: "Success",
                        detail: response.data.message,
                        life: 3000,
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: response.data.message,
                        life: 3000,
                    });
                }
            })
            .catch((err: AxiosError) => {
                if (err.status === 401) {
                    dispatch(logout());
                    persistor.purge(); // Clears persisted auth state
                    navigate("/admin/login");
                }
                errorHandler(err);
            })
            .finally(() => {
                navigate("/admin/settings");
            });
    }

    return (
        <div className="p-4 standalone-page-bg">
            <div className="flex items-center gap-2 mb-2 cursor-pointer w-max" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" />
                <h3 className="m-0 text-lg sm:text-xl">Back</h3>
            </div>
            <div className="table-margin inner-table-margin">
                <div className="grid grid-cols-12 gap-4 flex items-center justify-between table-header mt-2">
                    <div className="col-span-12">
                        <h2 className="card-title m-0 underline">Privacy Policy</h2>
                    </div>
                </div>
                {loading ? (
                    <div className="grid grid-cols-12 gap-4 mt-2">
                        <div className="lg:col-span-6 col-span-12">
                            <Skeleton width="140px" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="300px" borderRadius="8px" />
                        </div>
                        <div className="lg:col-span-6 col-span-12">
                            <Skeleton width="100px" height="1.1rem" className="mb-2" />
                            <Skeleton width="100%" height="300px" borderRadius="8px" />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-12 gap-4 flex items-center justify-between table-header mt-2">
                            <div className="lg:col-span-6 col-span-12">
                                <h4 className="card-title m-0 mb-2">In english language</h4>
                                <Editor value={englishText} onTextChange={(e) => setEnglishText(e.htmlValue ?? '')} className="bg-white h-full lg:h-[calc(100vh-210px)] editor-custom" />
                            </div>
                            <div className="lg:col-span-6 col-span-12">
                                <h4 className="card-title m-0 mb-2">ગુજરાતી મા</h4>
                                <Editor value={gujaratiText} onTextChange={(e) => setGujaratiText(e.htmlValue ?? '')} className="bg-white h-full lg:h-[calc(100vh-210px)] editor-custom" />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4 flex items-center justify-between table-header mt-4">
                            <div className="col-span-12 flex gap-2 justify-end">
                                <Button
                                    label="Submit"
                                    className="round-button"
                                    onClick={handleAdd}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
